import { useCallback, useMemo, useState } from "react";
import { useActiveFBOs } from "../containers/ActiveFBOContainer";
import { useApi } from "../containers/ApiContainer";
import { PlacementService } from "../services/PlacementService";
import { TripService } from "../services/TripService";
import { Aircraft, Tenant, TenantType, Trip, TripWithTenant } from "../types";
import { isInfinityDate } from "../utils/dateHelpers";
import { useTenants } from "./useTenants";

export interface CreateTripFormData {
  tailNumber: string;
  ownerManager: string;
  tenantType: string;
  selectedAircraft: Aircraft | null;
  selectedTenant: Tenant | null;
  arrivalDate: Date | null;
  departureDate: Date | string | null; // Can be Date or "infinity" string
  notes: string;
}

export interface CreateTripOptions {
  locationId?: string;
  locationType?: "hangar" | "ramp";
  useGapFilling?: boolean;
}

export interface UseCreateTripReturn {
  // Form state
  formData: CreateTripFormData;
  setFormData: (data: Partial<CreateTripFormData>) => void;

  // UI state
  loading: boolean;
  error: string | null;
  validationErrors: Record<string, string>;

  // Actions
  createTrip: (options?: CreateTripOptions) => Promise<Trip>;
  validateForm: (existingTrips?: TripWithTenant[]) => boolean;
  resetForm: () => void;
  setError: (error: string | null) => void;
  checkForOverlaps: (arrivalDate: Date | null, departureDate: Date | string | null, existingTrips: TripWithTenant[]) => string | null;

  // Derived state
  isFormValid: boolean;

  // Dependencies
  tenants: Tenant[];
}

const initialFormData: CreateTripFormData = {
  tailNumber: "",
  ownerManager: "",
  tenantType: TenantType.TRANSIENT,
  selectedAircraft: null,
  selectedTenant: null,
  arrivalDate: null,
  departureDate: null,
  notes: "",
};

export const useCreateTrip = (
  initialData?: Partial<CreateTripFormData>
): UseCreateTripReturn => {
  const { activeFBO } = useActiveFBOs();
  const { postgrest } = useApi();
  const { tenants } = useTenants();


  // Services
  const tripService = useMemo(() => new TripService(postgrest), [postgrest]);
  const placementService = useMemo(
    () => new PlacementService(postgrest),
    [postgrest]
  );

  // Form state
  const [formData, setFormDataState] = useState<CreateTripFormData>({
    ...initialFormData,
    ...initialData,
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Form data setter
  const setFormData = useCallback((data: Partial<CreateTripFormData>) => {
    setFormDataState((prev) => ({ ...prev, ...data }));

    // Clear validation errors for fields that are being updated
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      Object.keys(data).forEach((key) => {
        if (key in newErrors) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  }, []);

  // Helper function to find or create tenant
  const findOrCreateTenant = useCallback(
    async (
      tailNumber: string,
      tenantType: string,
      ownerManager: string,
      selectedAircraft: Aircraft | null
    ): Promise<string> => {
      // Check existing tenants first
      const existingTenant = tenants.find(
        (t) => t.tail_number?.toLowerCase() === tailNumber.toLowerCase()
      );
      if (existingTenant) {
        return existingTenant.id;
      }

      // For new tenant, require aircraft to be selected
      if (!selectedAircraft) {
        throw new Error("Aircraft selection is required for new tenants");
      }

      if (!activeFBO?.id) {
        throw new Error("No active FBO selected");
      }

      const { data: newTenant, error: tenantError } = await postgrest
        .from("tenant")
        .insert({
          fbo_id: activeFBO.id,
          tail_number: tailNumber.trim(),
          aircraft_id: selectedAircraft.id,
          type: tenantType,
          owner_manager: ownerManager.trim() || null,
        })
        .select()
        .single();

      if (tenantError) {
        throw new Error(`Failed to create tenant: ${tenantError.message}`);
      }

      return newTenant.id;
    },
    [tenants, activeFBO, postgrest]
  );

  // Check for overlapping trips
  const checkForOverlaps = useCallback(
    (
      arrivalDate: Date | null,
      departureDate: Date | string | null,
      existingTrips: TripWithTenant[]
    ): string | null => {
      if (!arrivalDate || !departureDate || !existingTrips.length) {
        return null;
      }

      const isCurrentInfinity = isInfinityDate(departureDate);

      // Check each existing trip for overlap
      for (const trip of existingTrips) {
        const existingArrival = new Date(trip.arrival_date);
        const isExistingInfinity = isInfinityDate(trip.departure_date);

        let hasOverlap = false;

        if (isCurrentInfinity || isExistingInfinity) {
          // Infinity trips overlap with all other trips for the same tenant
          hasOverlap = true;
        } else {
          // Both finite - use standard overlap logic
          const departureAsDate = typeof departureDate === 'string' ? new Date(departureDate) : departureDate;
          const existingDeparture = new Date(trip.departure_date);

          hasOverlap =
            (arrivalDate >= existingArrival && arrivalDate < existingDeparture) ||
            (departureAsDate > existingArrival && departureAsDate <= existingDeparture) ||
            (arrivalDate <= existingArrival && departureAsDate >= existingDeparture) ||
            (existingArrival <= arrivalDate && existingDeparture >= departureAsDate);
        }

        if (hasOverlap) {
          const formatDate = (date: Date) => {
            return date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            });
          };

          const departureStr = isExistingInfinity
            ? "No scheduled departure"
            : formatDate(new Date(trip.departure_date));

          return `Overlaps with existing trip: ${formatDate(existingArrival)} - ${departureStr}`;
        }
      }

      return null;
    },
    []
  );

  // Form validation
  const validateForm = useCallback((existingTrips?: TripWithTenant[]): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.tailNumber.trim()) {
      errors.tailNumber = "Tail number is required";
    }

    if (!formData.selectedAircraft && !formData.selectedTenant?.aircraft) {
      errors.aircraft =
        "Aircraft selection is required. Either select an existing tail number or manually choose an aircraft.";
    }

    if (!formData.arrivalDate) {
      errors.arrivalDate = "Arrival date is required";
    }

    if (!formData.departureDate) {
      errors.departureDate = "Departure date is required";
    }

    // Skip date comparison if departure is infinity (base tenant)
    if (
      formData.arrivalDate &&
      formData.departureDate &&
      !isInfinityDate(formData.departureDate) &&
      formData.arrivalDate >= formData.departureDate
    ) {
      errors.departureDate = "Departure must be after arrival";
    }

    // Check for overlaps with existing trips if provided
    if (existingTrips && existingTrips.length > 0) {
      const overlapMessage = checkForOverlaps(
        formData.arrivalDate,
        formData.departureDate,
        existingTrips
      );

      if (overlapMessage) {
        errors.overlap = overlapMessage;
        // Also set specific errors on date fields
        errors.arrivalDate = errors.arrivalDate || "Dates overlap with existing trip";
        errors.departureDate = errors.departureDate || "Dates overlap with existing trip";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, checkForOverlaps]);

  // Create trip
  const createTrip = useCallback(
    async (options: CreateTripOptions = {}): Promise<Trip> => {
      if (!validateForm()) {
        throw new Error("Form validation failed");
      }

      if (!activeFBO?.id) {
        throw new Error("No active FBO selected");
      }

      setLoading(true);
      setError(null);

      try {
        // Find or create tenant
        const tenantId = await findOrCreateTenant(
          formData.tailNumber,
          formData.tenantType,
          formData.ownerManager,
          formData.selectedAircraft || formData.selectedTenant?.aircraft || null
        );

        // Create trip data
        const tripData: Partial<Trip> = {
          tenant_id: tenantId,
          fbo_id: activeFBO.id,
          arrival_date: formData.arrivalDate!.toISOString(),
          // Handle infinity string or convert Date to ISO string
          departure_date: typeof formData.departureDate === 'string'
            ? formData.departureDate
            : formData.departureDate!.toISOString(),
          status: "scheduled" as const,
          notes: formData.notes.trim() || undefined,
        };

        let trip: Trip;

        // Create trip with or without location assignment
        if (options.locationId && options.locationType) {
          if (options.useGapFilling) {
            // Use regular createTrip + assignToLocationWithGapFilling for AddTripTab
            trip = await tripService.createTrip(tripData);
            await placementService.assignToLocationWithGapFilling(
              trip.id,
              options.locationId,
              options.locationType,
              formData.arrivalDate!.toISOString(),
              // Handle infinity string or convert Date to ISO string
              typeof formData.departureDate === 'string'
                ? formData.departureDate
                : formData.departureDate!.toISOString()
            );
          } else {
            // Use atomic createTripWithLocation for CreateTripDialog
            trip = await tripService.createTripWithLocation(
              tripData,
              options.locationId,
              options.locationType
            );
          }
        } else {
          // No location assignment
          trip = await tripService.createTrip(tripData);
        }

        return trip;
      } catch (err: any) {
        const errorMessage =
          err.message || "Failed to create trip. Please try again.";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [activeFBO, findOrCreateTenant, tripService, placementService, validateForm, formData]
  );

  // Reset form
  const resetForm = useCallback(() => {
    setFormDataState({ ...initialFormData, ...initialData });
    setValidationErrors({});
    setError(null);
  }, [initialData]);

  // Derived state
  const isFormValid = useMemo(() => {
    // Check if departure is infinity (base tenant)
    const hasValidDeparture = formData.departureDate && (
      isInfinityDate(formData.departureDate) ||
      (formData.arrivalDate && formData.departureDate instanceof Date && formData.arrivalDate < formData.departureDate)
    );

    return !!(
      formData.tailNumber.trim() &&
      (formData.selectedAircraft || formData.selectedTenant?.aircraft) &&
      formData.arrivalDate &&
      hasValidDeparture
    );
  }, [formData]);

  return {
    formData,
    setFormData,
    loading,
    error,
    validationErrors,
    createTrip,
    validateForm,
    resetForm,
    setError,
    checkForOverlaps,
    isFormValid,
    tenants,
  };
};
